import { Component, DestroyRef, Input, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NursingAuditService } from "src/app/services/nursing-audit.service";
import { SelectItem } from 'primeng/api';
import { LocationSelectComponent } from "src/app/shared/location-select/location-select.component";
import { ServiceGrpSelectComponent } from "src/app/shared/service-grp-select/service-grp-select.component";
import { AuthorizedLocationFilter, LocationAccessService, LocationFilter, LocationFilterEvent } from 'src/app/services/location-access.service';
import { ThemeColorService } from 'src/app/services/theme-color.service';
import { ThemePreferenceService } from 'src/app/services/theme-preference.service';

type ComparisonLocationFilter = AuthorizedLocationFilter & {
  changeValue?: boolean;
  duration: number;
  serviceGroup: string | string[];
  adminId?: string | string[];
  $sortOrder?: number;
};

@Component({
    selector: 'app-comparison-insights',
    templateUrl: './comparison-insights.component.html',
    styleUrls: ['./comparison-insights.component.scss'],
    standalone: false
})
export class ComparisonInsightsComponent {
  authService = inject(AuthService);
  private auditService = inject(NursingAuditService);
  private locationAccessService = inject(LocationAccessService);
  private themeColorService = inject(ThemeColorService);
  private themePreferenceService = inject(ThemePreferenceService);
  private destroyRef = inject(DestroyRef);
 
  filterA: ComparisonLocationFilter = {
    changeValue:true,
    duration: 90, 
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    serviceGroup: "All",
    adminId: "All",
  }
  filterB: ComparisonLocationFilter = {
    changeValue:true,
    duration: 90, 
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    serviceGroup: "All",
    adminId: "All",
  }
  graphFilter: ComparisonLocationFilter = {
    duration: 90, 
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    serviceGroup: "All",
    adminId: "All",
  }
  adminFilter: ComparisonLocationFilter = {
    changeValue:true,
    duration:7,
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    serviceGroup: "All",
  }
  grp1Interaction:any = {
    totalResolution: 0,
    highRiskCount:0,   
  }
  grp1Admin:any = {
    adminCount:0
  }
  grp1DiscrepRate:any = {
    discrepRate:0
  }
  grp2Interaction:any = {
    totalResolution: 0,
    highRiskCount:0,  
  }
  grp2Admin:any = {
    adminCount:0
  }
  grp2DiscrepRate:any = {
    discrepRate:0
  }
  selectedDays = 90;
  days: SelectItem[] = [
    { label: '90 Days', value: 90 },
    { label: '30 Days', value: 30 },
    { label: '7 Days', value: 7 }
  ];
  date: any = {}
  loading = false
  adminOptions: any = [];
  comparisonData:any;
  comparisonOptions:any;
  auditorSelection:any = {};
  loc_filter_breakdown= 'Region';
  chartDataGr1:any;
  chartDataGr2:any;
  @Input() deId=false;
  @ViewChild('LocationA') LocationA!:LocationSelectComponent;
  @ViewChild('LocationB') LocationB!:LocationSelectComponent;
  @ViewChild('serviceGroupA') serviceGroupA!:ServiceGrpSelectComponent
  @ViewChild('serviceGroupB') serviceGroupB!:ServiceGrpSelectComponent

  constructor() {
    this.themePreferenceService.preference$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (Array.isArray(this.chartDataGr1) && Array.isArray(this.chartDataGr2)) {
          this.loadComparisonChart();
        }
      });
  }

  refreshData(){
    this.fetchAdminIDA(this.adminFilter)
    this.fetchAdminIDB(this.adminFilter)
  }

  onLocationChangedGroup1(event: LocationFilterEvent){
    const sanitized = this.applyAuthorizedLocationFilter(this.filterA, event);
    this.updateLocationBreakdown(sanitized, this.filterA);
    this.filterA.$sortOrder = -1;
    this.fetchAdminIDA(this.filterA)
  }

  onLocationChangedGroup2(event: LocationFilterEvent){
    //this.loading=true;
    const sanitized = this.applyAuthorizedLocationFilter(this.filterB, event);
    this.updateLocationBreakdown(sanitized, this.filterB);
    this.filterB.$sortOrder = -1;
    this.fetchAdminIDB(this.filterB)
  }

  private applyAuthorizedLocationFilter(filter: ComparisonLocationFilter, event: LocationFilterEvent): LocationFilter {
    const sanitized = this.locationAccessService.sanitizeSelectedFilters(event, this.authService.user);
    this.locationAccessService.applyAuthorizedFilters(filter, sanitized, this.authService.user);
    return sanitized;
  }

  private updateLocationBreakdown(sanitized: LocationFilter, filter: ComparisonLocationFilter): void {
    if (this.locationAccessService.hasAll(sanitized.region)) {
      filter.adminId = [];
      this.auditorSelection = {};
    }
    this.loc_filter_breakdown = this.locationAccessService.getFilterBreakdown(sanitized, {
      allLarge: 'Region',
      regionSelected: 'Campus',
      campusSelected: 'Sites',
      facilitySelected: 'Location Group',
      locationGroupSelected: 'Location Group'
    });
  }

  onServiceGrpChangedGroup1(event: any){
    this.filterA.serviceGroup = event.serviceGroup;
    this.fetchAdminIDA(this.filterA)
  }

  onServiceGrpChangedGroup2(event: any){
    this.filterB.serviceGroup = event.serviceGroup;
    this.fetchAdminIDB(this.filterB)
  }

  fetchAdminIDA(filter: any){
    this.auditService.fetchAdminID(filter).subscribe((res) => {
       this.adminOptions = [{label:"All User IDs", value:"All"}].concat(
            (res as any)['adminData'].map((adminId: any) => {
                return {label: adminId.label, value:adminId.value}
            })
        ) 
        this.filterA.adminId = ["All"]
    })
  }

  fetchAdminIDB(filter: any){
    this.auditService.fetchAdminID(filter).subscribe((res) => {
       this.adminOptions = [{label:"All User IDs", value:"All"}].concat(
            (res as any)['adminData'].map((adminId: any) => {
                return {label: adminId.label, value:adminId.value}
            })
        ) 
        this.filterB.adminId = ["All"]
        this.date = (res as any)['date'];
        this.date.startDate = new Date(this.gdfs(this.date.startDate)).setHours(0,0,0);
        this.date.endDate = new Date(this.date.endDate).setHours(23,59,59);
    })
  }

  changeAdminIDGroup1(event: any){
    if (event.value.length == 0){
      this.filterA.adminId = ["All"]
      }
      else if (event.value[event.value.length - 1] == 'All'){
        this.filterA.adminId = ['All']
      }
      else{
        if (Array.isArray(this.filterA.adminId) && event.value.includes('All')){
          this.filterA.adminId.splice(this.filterA.adminId.indexOf('All'), 1)
        } 
      }
  }

  changeAdminIDGroup2(event: any){
    if (event.value.length == 0){
      this.filterB.adminId = ["All"]
      }
      else if (event.value[event.value.length - 1] == 'All'){
        this.filterB.adminId = ['All']
      }
      else{
        if (Array.isArray(this.filterB.adminId) && event.value.includes('All')){
          this.filterB.adminId.splice(this.filterB.adminId.indexOf('All'), 1)
        } 
      }
  }

  submitResults(){
    this.loading = true
    this.auditService.getComparisonData(this.filterA ).subscribe((res) => {
      if((res as any)['interactionData'][0] == undefined){
        this.grp1Interaction.totalResolution = 0;
        this.grp1Interaction.highRiskCount = 0;
      }else{
        this.grp1Interaction = (res as any)['interactionData'][0]
      };
      this.grp1DiscrepRate = res
      this.grp1Admin = (res as any)['adminCountData']
      this.chartDataGr1 = (res as any)['comparisonData']
      this.auditService.getComparisonData(this.filterB).subscribe((res2) => {
        if((res2 as any)['interactionData'][0] == undefined){
          this.grp2Interaction.totalResolution = 0;
          this.grp2Interaction.highRiskCount = 0;
        }else{
          this.grp2Interaction = (res2 as any)['interactionData'][0]
        };
        //this.grp2Interaction = res2['interactionData'][0]
        this.grp2DiscrepRate = res2
        this.grp2Admin = (res2 as any)['adminCountData']
        this.chartDataGr2 = (res2 as any)['comparisonData']
        this.loadComparisonChart()
      })
    })
    
  }

  onReset(){
    this.LocationA.onClickReset()
    this.LocationB.onClickReset()
    
    this.serviceGroupA.onReset()
    this.serviceGroupB.onReset()

    this.filterA.serviceGroup = ['All'];
    this.filterB.serviceGroup = ['All'];

    this.filterA.adminId = ['All'];
    this.filterB.adminId = ['All'];
    this.submitResults()
  }

  loadComparisonChart(){
      const values1 = []
      const values2 = []
      const labels = []
      const color1 = []
      const color2 = []
      const groupAColor = this.themeColorService.resolve('--chart-comparison-group-a')
      const groupBColor = this.themeColorService.resolve('--chart-comparison-group-b')
      const chartTextColor = this.themeColorService.resolve('--text-primary')
      const chartGridColor = this.themeColorService.resolve('--chart-grid-line')
      if(this.chartDataGr1.length == 0){
        values1.push(0)
        for (const chartData of this.chartDataGr2) {
          const dates = this.getLabelString(chartData['date'])
          labels.push(dates)
        }
      }else{
        for (const chartData of this.chartDataGr1) {
                const dates = this.getLabelString(chartData['date'])
                values1.push(chartData['chartRate']) 
                labels.push(dates)
                color1.push(groupAColor)
        }
      }
      if(this.chartDataGr2.length == 0){
        values2.push(0)
        for (const chartData of this.chartDataGr2) {
          const dates = this.getLabelString(chartData['date'])
          labels.push(dates)
        }
      }else{
        for (const chartData of this.chartDataGr2) {
          const dates = this.getLabelString(chartData['date'])
          if(labels.includes(dates)){        
              values2.push(chartData['chartRate'])      
            color2.push(groupBColor)
          }
        }
      }
      this.comparisonData = {
        labels: labels,
        datasets: [{
          label: 'Group A',
          fill:false,
          borderColor: color1,
          data: values1,  
        },
        {
          label: 'Group B',
          fill:false,
          borderColor: color2,
          data: values2, 
        }
      ]
      };

    this.comparisonOptions = {
        stacked: false,
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
          title: {
            display: true,
            text: 'Daily High Risk Variance Rates',
            color: chartTextColor,
            font: {
              size: 18,
              family: 'Arial',
              weight: 'bold',
            },
            subtitle:{
              text:'(Total High Risk Variances/Total Resolutions)'
            }
          },
            legend: {
              display: true,
              labels: {
                color: chartTextColor
              }
            }
        },
        scales: {
            x: {
                ticks: {
                  color: chartTextColor
                },
                grid: {
                  color: chartGridColor
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                ticks: {
                  color: chartTextColor
                },
                grid: {
                  color: chartGridColor
                }
            },
          }
        }
          this.loading=false    
    }

    getLabelString(date: any) {
      date = new Date(date)
      return date.getMonth()+1 + "/" + date.getDate() + "/" + date.getFullYear()
    }

    updateLookbackPeriod(event: any) {
      const lookbackPeriod = event["value"];
      this.filterA.duration = lookbackPeriod;
      this.filterB.duration = lookbackPeriod;
      this.adminFilter.duration = lookbackPeriod;
      this.refreshData();
      this.submitResults()
    }

    gdfs(s: string){
      const d = s.substring(0,10).split('-')
      return d[1] + '-' + d[2] + '-' + d[0]
    }

    updatedDate(filter: any){
      this.date.startDate = new Date(this.gdfs(filter.startDate)).setHours(0,0,0);
      this.date.endDate = new Date(this.gdfs(filter.endDate)).setHours(23,59,59);
    }
}
